/**
 * CompanyRegistrationCreatePage — creates a new company registration
 * request. Uses the cascading dropdowns loaded from the Company Setup
 * Inputs CRUD (`/admin/company-setup-inputs/active-tree`).
 *
 * (iter-220, Feb 2026)
 *
 * Fields:
 *  - Service name: hardcoded to "تسجيل الشركات" (display-only).
 *  - Optional notes.
 *  - Request source + request date.
 *  - Cascading: Country → Region → Authority.
 *  - Client: name, phone, email.
 *  - Task executor (admin/staff picker, mandatory).
 *  - Attachments (PDF/images, max 15 × 8MB, each needs a label).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Paperclip, X, User, FileText, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { AgentPageShell } from './agent/AgentPageShell';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const token = sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token');
  return { Authorization: 'Bearer ' + token };
};

const REQUEST_SOURCES = [
  { key: 'whatsapp',  ar: 'واتساب',        en: 'WhatsApp' },
  { key: 'phone',     ar: 'اتصال هاتفي',   en: 'Phone call' },
  { key: 'email',     ar: 'بريد إلكتروني', en: 'Email' },
  { key: 'in_person', ar: 'حضور شخصي',    en: 'In person' },
  { key: 'referral',  ar: 'إحالة',         en: 'Referral' },
  { key: 'other',     ar: 'أخرى',          en: 'Other' },
];

const MAX_ATTACHMENTS = 15;
const MAX_ATTACHMENT_MB = 8;

// iter-237.5 (Feb 2026) — Field-level validators. Applied on submit + as
// a "reddish" outline on invalid inputs. Rule per user: whenever we ask for
// a phone or email, we MUST validate them. Empty is allowed (fields are
// optional), non-empty MUST match the pattern.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Accept international format: optional leading +, 7-15 digits (E.164),
// spaces / dashes tolerated but stripped when saved.
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;
const isEmailValid = (v) => !v || EMAIL_RE.test(String(v).trim());
const isPhoneValid = (v) => !v || PHONE_RE.test(String(v).trim());

export default function CompanyRegistrationCreatePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    notes: '',
    request_source: 'whatsapp',
    request_date: today,
    country_id: '',
    region_id: '',
    authority_id: '',
    client_name: '',
    client_phone: '',
    client_email: '',
    assignee_id: '',
    assignee_name: '',
    assignee_email: '',
    attachments: [],
    // iter-264c — Activities (up to 10, at least 1 required, free text).
    activities: [''],
    // iter-264c — Three requested license names (all required).
    license_names: ['', '', ''],
    // iter-264c — Ownership: 'single' or 'partners' + partner_count when partners.
    ownership_type: '',
    partner_count: '',
  });
  const [saving, setSaving] = useState(false);

  const [tree, setTree] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(true);

  // Load cascading inputs + staff list
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(API + '/api/admin/company-setup-inputs/active-tree', { headers: authHeader() });
        setTree((res.data && res.data.data) || []);
      } catch {
        toast.error(t('فشل تحميل الدول', 'Failed to load countries'));
      } finally {
        setLoadingTree(false);
      }
      try {
        const res = await axios.get(API + '/api/admin/assigned-tasks/staff', { headers: authHeader() });
        setStaff((res.data && res.data.data) || []);
      } catch {
        // ok — staff picker will just be empty
      } finally {
        setLoadingStaff(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived cascading lists
  const country = useMemo(() => tree.find((c) => c.id === form.country_id) || null, [tree, form.country_id]);
  const regions = country ? (country.regions || []) : [];
  const region  = regions.find((r) => r.id === form.region_id) || null;
  const authorities = region ? (region.authorities || []) : [];

  const onAttach = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (form.attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(t('الحد الأقصى ' + MAX_ATTACHMENTS + ' مرفقات', 'Max ' + MAX_ATTACHMENTS + ' attachments'));
      return;
    }
    const accepted = /^(application\/pdf|image\/(png|jpe?g|webp|heic|heif))$/i;
    const newAtts = [];
    for (const f of files) {
      if (!accepted.test(f.type)) {
        toast.error(t(f.name + ': نوع غير مدعوم — PDF أو صور فقط', f.name + ': unsupported — PDF or image only'));
        continue;
      }
      if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        toast.error(t(f.name + ': أكبر من ' + MAX_ATTACHMENT_MB + ' ميجا', f.name + ': exceeds ' + MAX_ATTACHMENT_MB + ' MB'));
        continue;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      newAtts.push({ name: f.name, label: '', mime_type: f.type, size: f.size, data_url: dataUrl });
    }
    if (newAtts.length) {
      setForm((s) => ({ ...s, attachments: [...s.attachments, ...newAtts] }));
    }
  };

  const removeAttachment = (idx) => {
    setForm((s) => ({ ...s, attachments: s.attachments.filter((_, i) => i !== idx) }));
  };
  const renameAttachment = (idx, label) => {
    setForm((s) => ({ ...s, attachments: s.attachments.map((a, i) => i === idx ? { ...a, label } : a) }));
  };

  const onSubmit = useCallback(async () => {
    // Validation
    if (!form.country_id || !form.region_id || !form.authority_id) {
      toast.error(t('اختر الدولة والإمارة والجهة', 'Pick country, region and authority'));
      return;
    }
    if (!form.client_name.trim()) {
      toast.error(t('اسم العميل مطلوب', 'Client name required'));
      return;
    }
    if (!isEmailValid(form.client_email)) {
      toast.error(t('صيغة البريد الإلكتروني للعميل غير صحيحة', 'Client email format is invalid'));
      return;
    }
    if (!isPhoneValid(form.client_phone)) {
      toast.error(t('صيغة رقم هاتف العميل غير صحيحة', 'Client phone format is invalid'));
      return;
    }
    if (!form.assignee_id) {
      toast.error(t('اختر منفذ المهمة', 'Pick a task executor'));
      return;
    }
    // iter-264c — Validate activities (≥1 non-empty, ≤10)
    const cleanActivities = (form.activities || [])
      .map((s) => (s || '').trim())
      .filter(Boolean);
    if (cleanActivities.length === 0) {
      toast.error(t('أضف نشاطاً واحداً على الأقل', 'Add at least one activity'));
      return;
    }
    if (cleanActivities.length > 10) {
      toast.error(t('الحد الأقصى 10 أنشطة', 'Max 10 activities'));
      return;
    }
    // iter-264c — Validate license names (exactly 3 non-empty)
    const cleanLicenseNames = (form.license_names || []).map((s) => (s || '').trim());
    if (cleanLicenseNames.some((n) => !n) || cleanLicenseNames.length !== 3) {
      toast.error(t('يجب إدخال 3 أسماء للرخصة', 'Three license names are required'));
      return;
    }
    // iter-264c — Ownership type
    if (!form.ownership_type) {
      toast.error(t('اختر نوع الملكية', 'Pick ownership type'));
      return;
    }
    let partnerCountNum = 0;
    if (form.ownership_type === 'partners') {
      partnerCountNum = parseInt(form.partner_count, 10) || 0;
      if (partnerCountNum < 2) {
        toast.error(t('عدد الشركاء يجب أن يكون 2 أو أكثر', 'Partner count must be at least 2'));
        return;
      }
    }
    const missingLabel = form.attachments.findIndex((a) => !(a.label || '').trim());
    if (missingLabel >= 0) {
      toast.error(t('أدخل اسماً وصفياً لكل مرفق', 'Please label every attachment'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        notes: form.notes,
        request_source: form.request_source,
        request_date: form.request_date ? new Date(form.request_date).toISOString() : null,
        country_id: form.country_id,
        region_id: form.region_id,
        authority_id: form.authority_id,
        client: { name: form.client_name, phone: form.client_phone, email: form.client_email },
        assignee_id: form.assignee_id,
        assignee_name: form.assignee_name,
        assignee_email: form.assignee_email,
        attachments: form.attachments,
        // iter-264c — new business fields
        activities: cleanActivities,
        license_names: cleanLicenseNames,
        ownership: form.ownership_type === 'partners'
          ? { type: 'partners', partner_count: partnerCountNum }
          : { type: 'single' },
      };
      const res = await axios.post(API + '/api/admin/company-registrations', payload, { headers: authHeader() });
      const code = res.data?.data?.request_code;
      toast.success(t('تم إنشاء الطلب ' + (code || ''), 'Request ' + (code || '') + ' created'));
      const id = res.data?.data?.id;
      if (id) navigate('/manage/cloudgm-agent/company-registration/track/' + id);
      else navigate('/manage/cloudgm-agent/company-registration/track');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الإرسال', 'Submit failed'));
    } finally {
      setSaving(false);
    }
  }, [form, isAr, navigate]);

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'تسجيل الشركات', label_en: 'Company Registration', path: '/manage/cloudgm-agent/company-registration' },
    { label_ar: 'تسجيل شركة جديدة', label_en: 'Register New Company', path: null },
  ];

  return (
    <AgentPageShell breadcrumb={breadcrumb} onBack={() => navigate('/manage/cloudgm-agent/company-registration')}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Service name (fixed) */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <FileText className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <div className="text-xs text-[#71717A]">{t('الخدمة', 'Service')}</div>
              <div className="text-lg font-bold text-white">
                {t('تسجيل الشركات', 'Company Registration')}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-[#71717A] mb-2">
              {t('ملاحظات على الطلب (اختياري)', 'Notes on request (optional)')}
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
              data-testid="notes-input"
            />
          </div>
        </div>

        {/* Request source + date */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-lg font-bold text-white mb-4">{t('مصدر الطلب', 'Request source')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#71717A] mb-2">{t('المصدر', 'Source')}</label>
              <select
                value={form.request_source}
                onChange={(e) => setForm({ ...form, request_source: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                data-testid="source-select"
              >
                {REQUEST_SOURCES.map((s) => (
                  <option key={s.key} value={s.key}>{isAr ? s.ar : s.en}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#71717A] mb-2">{t('تاريخ مصدر الطلب', 'Request date')}</label>
              <Input
                type="date"
                value={form.request_date}
                onChange={(e) => setForm({ ...form, request_date: e.target.value })}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                dir="ltr"
                data-testid="date-input"
              />
            </div>
          </div>
        </div>

        {/* Cascading dropdowns */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-cyan-400" />
            {t('الدولة، الإمارة، الجهة', 'Country · Region · Authority')}
          </h3>
          {loadingTree ? (
            <div className="flex items-center gap-2 text-[#71717A]"><Loader2 className="h-4 w-4 animate-spin" /> {t('جاري التحميل…', 'Loading…')}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-[#71717A] mb-2">{t('الدولة', 'Country')} *</label>
                <select
                  value={form.country_id}
                  onChange={(e) => setForm({ ...form, country_id: e.target.value, region_id: '', authority_id: '' })}
                  className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
                  data-testid="country-select"
                >
                  <option value="">{t('-- اختر --', '-- pick --')}</option>
                  {tree.map((c) => (
                    <option key={c.id} value={c.id}>{isAr ? (c.name_ar || c.name_en) : (c.name_en || c.name_ar)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#71717A] mb-2">{t('الإمارة / المنطقة', 'Region')} *</label>
                <select
                  value={form.region_id}
                  onChange={(e) => setForm({ ...form, region_id: e.target.value, authority_id: '' })}
                  disabled={!country}
                  className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2 disabled:opacity-40"
                  data-testid="region-select"
                >
                  <option value="">{t('-- اختر --', '-- pick --')}</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>{isAr ? (r.name_ar || r.name_en) : (r.name_en || r.name_ar)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#71717A] mb-2">{t('الجهة', 'Authority')} *</label>
                <select
                  value={form.authority_id}
                  onChange={(e) => setForm({ ...form, authority_id: e.target.value })}
                  disabled={!region}
                  className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2 disabled:opacity-40"
                  data-testid="authority-select"
                >
                  <option value="">{t('-- اختر --', '-- pick --')}</option>
                  {authorities.map((a) => (
                    <option key={a.id} value={a.id}>{isAr ? (a.name_ar || a.name_en) : (a.name_en || a.name_ar)}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Client info */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-amber-400" />
            {t('المُقدَّم لأجله الطلب', 'Requested for (Client)')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-[#71717A] mb-2">{t('الاسم', 'Name')} *</label>
              <Input
                value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                data-testid="client-name-input"
              />
            </div>
            <div>
              <label className="block text-sm text-[#71717A] mb-2">{t('رقم الهاتف', 'Phone')}</label>
              <Input
                type="tel"
                inputMode="tel"
                value={form.client_phone}
                onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                className={`bg-[#0a0a0a] text-white rounded-xl border ${
                  isPhoneValid(form.client_phone) ? 'border-[#2a2a4e]' : 'border-red-500/60'
                }`}
                dir="ltr"
                placeholder="+971 5X XXX XXXX"
                data-testid="client-phone-input"
              />
              {!isPhoneValid(form.client_phone) && (
                <p className="mt-1 text-[11px] text-red-400" data-testid="client-phone-error">
                  {t('صيغة رقم الهاتف غير صحيحة (7-20 خانة)', 'Invalid phone format (7-20 digits)')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm text-[#71717A] mb-2">{t('البريد الإلكتروني', 'Email')}</label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                className={`bg-[#0a0a0a] text-white rounded-xl border ${
                  isEmailValid(form.client_email) ? 'border-[#2a2a4e]' : 'border-red-500/60'
                }`}
                dir="ltr"
                placeholder="name@example.com"
                data-testid="client-email-input"
              />
              {!isEmailValid(form.client_email) && (
                <p className="mt-1 text-[11px] text-red-400" data-testid="client-email-error">
                  {t('صيغة البريد الإلكتروني غير صحيحة', 'Invalid email format')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Task executor */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-lg font-bold text-white mb-4">{t('منفذ المهمة', 'Task executor')}</h3>
          {loadingStaff ? (
            <Loader2 className="h-4 w-4 animate-spin text-[#71717A]" />
          ) : (
            <select
              value={form.assignee_id}
              onChange={(e) => {
                const s = staff.find((x) => (x.id || x._id) === e.target.value);
                setForm({
                  ...form,
                  assignee_id: e.target.value,
                  assignee_name: s ? (s.name || s.full_name || s.email || '') : '',
                  assignee_email: s ? (s.email || '') : '',
                });
              }}
              className="w-full bg-[#0a0a0a] border border-[#2a2a4e] text-white rounded-xl px-4 py-2"
              data-testid="executor-select"
            >
              <option value="">{t('-- اختر منفذ --', '-- pick executor --')}</option>
              {staff.map((s) => (
                <option key={s.id || s._id} value={s.id || s._id}>
                  {(s.name || s.full_name || s.email)} — {s.email}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* iter-264c — Company activities (multi, ≥1, ≤10, free text) */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="activities-section">
          <h3 className="text-lg font-bold text-white mb-2">
            {t('أنشطة الشركة', 'Company Activities')} <span className="text-emerald-400 text-sm">*</span>
          </h3>
          <p className="text-xs text-[#71717A] mb-4">
            {t('نشاط واحد إجباري على الأقل — حتى 10 أنشطة', 'At least one activity required — up to 10')}
          </p>
          <div className="space-y-2">
            {form.activities.map((val, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-xs text-[#71717A] w-6 font-mono">{idx + 1}.</span>
                <Input
                  value={val}
                  onChange={(e) => setForm((s) => ({
                    ...s,
                    activities: s.activities.map((v, i) => i === idx ? e.target.value : v),
                  }))}
                  placeholder={t('اسم النشاط', 'Activity name')}
                  className="flex-1 bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  data-testid={`activity-input-${idx}`}
                />
                {form.activities.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setForm((s) => ({
                      ...s,
                      activities: s.activities.filter((_, i) => i !== idx),
                    }))}
                    className="w-9 h-9 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"
                    data-testid={`activity-remove-${idx}`}
                  >
                    <X className="h-4 w-4 text-red-400" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {form.activities.length < 10 && (
            <button
              type="button"
              onClick={() => setForm((s) => ({ ...s, activities: [...s.activities, ''] }))}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 text-sm"
              data-testid="activity-add"
            >
              + {t('إضافة نشاط', 'Add activity')}
            </button>
          )}
        </div>

        {/* iter-264c — 3 mandatory license names */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="license-names-section">
          <h3 className="text-lg font-bold text-white mb-2">
            {t('أسماء الرخصة (3 أسماء)', 'License Names (3 names)')} <span className="text-emerald-400 text-sm">*</span>
          </h3>
          <p className="text-xs text-[#71717A] mb-4">
            {t('يجب إدخال ثلاثة أسماء مقترحة للرخصة بالترتيب', 'Enter three proposed license names in order')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {form.license_names.map((val, idx) => (
              <div key={idx}>
                <label className="block text-xs text-[#71717A] mb-1">
                  {t(`الاسم ${idx + 1}`, `Name ${idx + 1}`)}
                </label>
                <Input
                  value={val}
                  onChange={(e) => setForm((s) => ({
                    ...s,
                    license_names: s.license_names.map((v, i) => i === idx ? e.target.value : v),
                  }))}
                  placeholder={t('الاسم المقترح', 'Proposed name')}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl"
                  data-testid={`license-name-input-${idx}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* iter-264c — Ownership type (single / partners) */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="ownership-section">
          <h3 className="text-lg font-bold text-white mb-4">
            {t('نوع الملكية', 'Ownership type')} <span className="text-emerald-400 text-sm">*</span>
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <button
              type="button"
              onClick={() => setForm((s) => ({ ...s, ownership_type: 'single', partner_count: '' }))}
              className={
                'p-4 rounded-xl border-2 text-sm font-semibold transition-all ' +
                (form.ownership_type === 'single'
                  ? 'bg-emerald-500/15 border-emerald-500 text-emerald-100'
                  : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
              }
              data-testid="ownership-single-btn"
            >
              {t('مالك واحد', 'Single owner')}
            </button>
            <button
              type="button"
              onClick={() => setForm((s) => ({ ...s, ownership_type: 'partners' }))}
              className={
                'p-4 rounded-xl border-2 text-sm font-semibold transition-all ' +
                (form.ownership_type === 'partners'
                  ? 'bg-cyan-500/15 border-cyan-500 text-cyan-100'
                  : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20')
              }
              data-testid="ownership-partners-btn"
            >
              {t('شركاء', 'Partners')}
            </button>
          </div>
          {form.ownership_type === 'partners' && (
            <div className="pt-2" data-testid="partner-count-wrapper">
              <label className="block text-xs text-[#71717A] mb-2">
                {t('عدد الشركاء', 'Number of partners')} <span className="text-emerald-400">*</span>
              </label>
              <Input
                type="number"
                min="2"
                value={form.partner_count}
                onChange={(e) => setForm((s) => ({ ...s, partner_count: e.target.value }))}
                placeholder="2"
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl max-w-xs"
                dir="ltr"
                data-testid="partner-count-input"
              />
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-purple-400" />
            {t('المرفقات', 'Attachments')}
          </h3>
          {form.attachments.length > 0 && (
            <div className="space-y-2 mb-3">
              {form.attachments.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-[#0a0a0a] rounded-xl border border-[#2a2a4e]" data-testid={'attach-' + idx}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[#606060] truncate">{a.name}</div>
                    <Input
                      value={a.label}
                      onChange={(e) => renameAttachment(idx, e.target.value)}
                      placeholder={t('اسم وصفي (مطلوب)', 'Descriptive label (required)')}
                      className="mt-1 bg-transparent border-[#2a2a4e] text-white text-sm rounded-lg h-8"
                      data-testid={'attach-label-' + idx}
                    />
                  </div>
                  <button
                    onClick={() => removeAttachment(idx)}
                    className="w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center"
                    data-testid={'attach-remove-' + idx}
                  >
                    <X className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label
            htmlFor="attach-input"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 cursor-pointer text-sm"
            data-testid="attach-add"
          >
            <Paperclip className="h-4 w-4" />
            {t('إضافة مرفق', 'Add attachment')}
          </label>
          <input
            id="attach-input"
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => { onAttach(e.target.files); e.target.value = ''; }}
          />
          <p className="text-xs text-[#606060] mt-2">
            {t('PDF أو صور • حد أقصى ' + MAX_ATTACHMENTS + ' مرفقات • ' + MAX_ATTACHMENT_MB + ' ميجا لكل ملف',
               'PDF or image • max ' + MAX_ATTACHMENTS + ' attachments • ' + MAX_ATTACHMENT_MB + ' MB each')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 pb-8">
          <Button
            onClick={() => navigate('/manage/cloudgm-agent/company-registration')}
            variant="outline"
            className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('رجوع', 'Back')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 px-8"
            data-testid="submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('بدء تسجيل الشركة', 'Start Company Registration')}
          </Button>
        </div>
      </div>
    </AgentPageShell>
  );
}
